/**
 * Custom element for slider
 */
export class Slider extends HTMLElement {
    static observedAttributes: string[] = ['auto-scroll-interval'];
    private autoScroll: number | undefined;

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }

    attributeChangedCallback(attrName: string, oldValue: string, newValue: string) {
        if (newValue !== oldValue) {
            if (attrName === 'auto-scroll-interval') {
                this.autoScrollInterval = newValue; // Explicitly call the setter
            }
        }
    }

    connectedCallback() {
        const template = document.getElementById('auto-slider-template') as HTMLTemplateElement;
        const node = document.importNode(template.content, true);
        this.shadowRoot!.appendChild(node);

		// Move all child slide elements into a parent div with class 'slides'
		const slideContainer = this.shadowRoot!.getElementById('slides') as HTMLDivElement;

		const slides = Array.from(this.children) as HTMLElement[];

		if (!slides.length) {
			return;
		}

		for (let i = 0; i < slides.length; i++) {
			const slide = slides[i];
			slide.classList.add('slide');
            if (i == slides.length - 1) {
                slide.classList.add('last-slide');
            }
			slideContainer.appendChild(slide);

			const slideButton = document.createElement('a');
			slideButton.classList.add('slide-button', 'slide-button-' + (i + 1));
			if (i == 0) {
				slideButton.classList.add('checked');
			}
			// Scroll by the distance between slide starts. Measuring two slides
			// includes the gap, and using that distance for every slide — including
			// the last — keeps the movement forward. The last slide used to set
			// scrollLeft from its viewport edge, which is smaller than the current
			// scroll offset and jumps backward.
			slideButton.addEventListener('click', function(e) {
				e.preventDefault();
				this.parentElement!.querySelector('.slide-button.checked')!.classList.remove('checked');
				this.classList.add('checked');

				const step = slides.length > 1
					? slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left
					: 0;
				slideContainer.scrollLeft = step * i;
			});
			slideContainer.appendChild(slideButton);
		}
    }

    disconnectedCallback() {
        this.stopAutoScroll();
    }

    get autoScrollInterval() {
        return this.getAttribute('auto-scroll-interval') || '';
    }

    set autoScrollInterval(value: string) {
		this.stopAutoScroll();
        if (value) {
            this.setAttribute('auto-scroll-interval', value);
			this.startAutoScroll();
        } else {
            this.removeAttribute('auto-scroll-interval');
        }
    }

    startAutoScroll() {
        const autoSlideInterval = this.autoScrollInterval;

		if (!autoSlideInterval) {
			return;
		}
        const autoSlideIntervalNum = parseInt(autoSlideInterval, 10);

        this.autoScroll = setInterval(() => {
            // Defensive check: Verify element is still connected and shadowRoot exists
            if (!this.isConnected || !this.shadowRoot) {
                this.stopAutoScroll();
                return;
            }

            const slides = this.shadowRoot.getElementById('slides') as HTMLDivElement | null;
            if (!slides) {
                this.stopAutoScroll();
                return;
            }

            var nextSlideBtn = slides.querySelector('.slide-button.checked ~ .slide-button') as HTMLAnchorElement | null;
            if (nextSlideBtn) {
                nextSlideBtn.click();
            } else {
                const firstSlideBtn = slides.querySelector('.slide-button') as HTMLAnchorElement | null;
                if (firstSlideBtn) {
                    firstSlideBtn.click();
                }
            }
        }, autoSlideIntervalNum);
    }

    stopAutoScroll() {
        if (this.autoScroll) {
            clearInterval(this.autoScroll);
            this.autoScroll = undefined;
        }
    }
}

customElements.define('auto-slider', Slider);
